import { useEffect, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Box, BoxRow } from 'components/common/box';
import {
  EmptyIndicator,
  LoadingIndicator,
  Pagination
} from 'components/common';
import { EventLogHeader, EventLogEntry } from '.';

import type { Course, EventLogTypeGroup } from 'shared-types';
import type { GetEventsRes } from 'shared-types/responses';

export type UpdateEvents = ({
  course,
  page,
  type,
  searchUser,
  throwError
}: {
  course?: number | string;
  page?: number;
  type?: string;
  searchUser?: string;
  throwError?: boolean;
}) => Promise<void>;

interface Props {
  achievementId?: number;
  authStrategyId?: number;
  calendarEventId?: number;
  competencyId?: number;
  competencyProfileId?: number;
  course?: number | string;
  courses?: Course[];
  groupId?: number;
  individualAchievementId?: number;
  integrationId?: number;
  isAdminEvent?: boolean;
  isApiRoutesEvent?: boolean;
  isAuthStrategiesEvent?: boolean;
  isChangelogEvent?: boolean;
  isClientPageEvent?: boolean;
  isIntegrationChangelogEvent?: boolean;
  isIntegrationsSharedChangelogEvent?: boolean;
  onlyEventsOfType?: EventLogTypeGroup;
  pathId?: number;
  targetUserId?: number;
  userId?: number;
  userSearch?: boolean;
}

const EventLog = ({
  achievementId,
  authStrategyId,
  calendarEventId,
  competencyId,
  competencyProfileId,
  course,
  courses,
  groupId,
  individualAchievementId,
  integrationId,
  isAdminEvent = false,
  isApiRoutesEvent = false,
  isAuthStrategiesEvent = false,
  isChangelogEvent = false,
  isClientPageEvent = false,
  isIntegrationChangelogEvent = false,
  isIntegrationsSharedChangelogEvent = false,
  onlyEventsOfType,
  pathId,
  targetUserId,
  userId,
  userSearch = false
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages, user } = useReduxState();

  const [state, setState] = useState({
    course: course,
    events: [] as GetEventsRes['events'],
    loading: true,
    pages: 0,
    page: 1,
    type: null,
    searchUser: ''
  });

  useEffect(() => {
    updateEvents({});
  }, []);

  const updateEvents = async ({
    course,
    page,
    type,
    searchUser,
    throwError = false
  }: {
    course?: number | string;
    page?: number;
    type?: string;
    searchUser?: string;
    throwError?: boolean;
  }) => {
    const courseValue = course || state.course;
    const pageValue = page || state.page;
    const typeValue = type || state.type;
    const searchValue =
      searchUser !== undefined ? searchUser : state.searchUser;

    setState({ ...state, loading: true });
    try {
      const url =
        `/admin/events?page=${pageValue}` +
        (isAdminEvent
          ? '&isAdminEvent=true'
          : isApiRoutesEvent
          ? '&isApiRoutesEvent=true'
          : isAuthStrategiesEvent
          ? '&isAuthStrategiesEvent=true'
          : isClientPageEvent
          ? '&isClientPageEvent=true'
          : isChangelogEvent
          ? '&isChangelogEvent=true'
          : isIntegrationChangelogEvent
          ? '&isIntegrationChangelogEvent=true'
          : isIntegrationsSharedChangelogEvent
          ? '&isIntegrationsSharedChangelogEvent=true'
          : '') +
        (groupId ? `&GroupId=${groupId}` : '') +
        (pathId ? `&PathId=${pathId}` : '') +
        (achievementId ? `&AchievementId=${achievementId}` : '') +
        (authStrategyId ? `&AuthStrategyId=${authStrategyId}` : '') +
        (individualAchievementId
          ? `&IndividualAchievementId=${individualAchievementId}`
          : '') +
        (calendarEventId ? `&CalendarEventId=${calendarEventId}` : '') +
        (competencyId ? `&CompetencyId=${competencyId}` : '') +
        (competencyProfileId
          ? `&CompetencyProfileId=${competencyProfileId}`
          : '') +
        (userId ? `&EventUserId=${userId}` : '') +
        (integrationId ? `&IntegrationId=${integrationId}` : '') +
        (courseValue && courseValue !== 'all'
          ? `&EventCourseId=${courseValue}`
          : '') +
        (typeValue && typeValue !== 'all' ? `&type=${typeValue}` : '') +
        (searchValue ? `&searchUser=${searchValue}` : '') +
        (targetUserId ? `&UserId=${targetUserId}` : '');

      const events = await api.get<GetEventsRes>(url);

      setState({
        ...state,
        events: events.data.events || [],
        loading: false,
        pages: events.data.pages,
        page: pageValue,
        course: courseValue,
        type: typeValue,
        searchUser: searchValue
      });
    } catch (err) {
      if (throwError) throw new Error(err);
      else {
        setState({ ...state, loading: false });
        await dispatch.handleErrorDispatch(err);
      }
    }
  };

  return (
    <div className="event-log">
      <Box
        headerContent={
          <EventLogHeader
            courses={courses}
            onlyEventsOfType={onlyEventsOfType}
            type={state.type}
            updateEvents={updateEvents}
            userSearch={userSearch}
          />
        }
        headerContentWrap={true}
        padding={0}
        title={messages.commonActivityLog}
      >
        {state.loading ? (
          <LoadingIndicator />
        ) : (
          <div className="events">
            {state.events.length ? (
              state.events.map((event) => (
                <BoxRow key={event.id}>
                  <EventLogEntry
                    event={event}
                    includeCalendarEvent={onlyEventsOfType !== 'calendar'}
                    includeCourse={onlyEventsOfType !== 'course'}
                    includePath={onlyEventsOfType !== 'path'}
                    showEventData={
                      event.data &&
                      (user?.roles.includes('superadmin') ||
                        [
                          'discard-chapter-changes',
                          'save-chapter-changes'
                        ].includes(event.type))
                    }
                  />
                </BoxRow>
              ))
            ) : (
              <EmptyIndicator
                description={messages.activitiesCommonNoActivitiesInfo}
                icon="list"
                title={messages.activitiesCommonNoActivities}
              />
            )}
          </div>
        )}
      </Box>

      <Pagination
        currentPage={state.page}
        numberOfPages={state.pages}
        onPageChange={updateEvents}
      />
    </div>
  );
};

export default EventLog;
